import React from 'react';
import { HashIcon, MailIcon, MarsIcon, MoonIcon, PhoneIcon, ShieldAlertIcon, SunIcon, VenusIcon } from 'lucide-react';
import { formatCurrency, formatDate, formatDay, formatDayRange, formatTime, fullName } from '../../utils/format';
import { balanceDue, documentChecklist, isCancelledRow, medicalReviewLabel } from '../../utils/registrations';
import { REMOVED_QUESTION, answerText, parseAnswerPairKey, questionText } from '../../utils/columns';
import { StatusBadge } from '../ui/StatusBadge';
import { CopyChip } from './CopyChip';
import { StatusNoteCell } from './StatusNoteCell';

/*
 * Cell renderers per column. Labels, sorting and filtering live in utils/columns. Money cells show
 * the values as received; booking-level figures say when the booking has more than one
 * participant ("shared by N"), because every row of that booking shows the same amounts.
 *
 * ctx = { density }.
 */

const Stack = ({ density, children }) => (
  <div className={density === 'compact' ? 'tw-flex tw-items-center tw-gap-2' : 'tw-flex tw-flex-col tw-items-start tw-gap-1'}>{children}</div>
);

const Empty = () => <span className="tw-text-neutral-400">—</span>;

const Tag = ({ children }) => (
  <span className="tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-md tw-bg-sand-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-neutral-500 tw-ring-1 tw-ring-inset tw-ring-neutral-200">
    {children}
  </span>
);

const Text = ({ value, tone = 'tw-text-neutral-900' }) =>
  value || value === 0 ? (
    <span className={`tw-block tw-truncate ${tone}`} title={String(value)}>
      {value}
    </span>
  ) : (
    <Empty />
  );

const SizeTag = ({ value }) =>
  value ? (
    <span className="tw-inline-flex tw-min-w-9 tw-justify-center tw-whitespace-nowrap tw-rounded-md tw-bg-sand-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-neutral-900 tw-ring-1 tw-ring-inset tw-ring-neutral-200">
      {value}
    </span>
  ) : (
    <Empty />
  );

const isNone = value => /^none$/i.test(String(value || '').trim());

const SharedBy = ({ r }) => {
  const n = r.money?.booking?.participantsOnBooking || 1;
  return n > 1 ? (
    <span className="tw-whitespace-nowrap tw-text-xs tw-text-neutral-400" title="Booking total, shared by every participant on this booking">
      shared by {n}
    </span>
  ) : null;
};

function ContactCell({ contact, density, emphasize }) {
  if (!contact || !contact.name) return <Empty />;
  const name = <span className={`tw-whitespace-nowrap ${emphasize ? 'tw-font-medium' : ''} tw-text-neutral-900`}>{contact.name}</span>;
  if (density === 'compact') {
    return (
      <div className="tw-flex tw-items-center tw-gap-1">
        <span className="tw-mr-1">{name}</span>
        <CopyChip icon={PhoneIcon} value={contact.phone} label="phone" />
        <CopyChip icon={MailIcon} value={contact.email} label="email" />
      </div>
    );
  }
  return (
    <div className="tw-min-w-0">
      {name}
      <div className="-tw-ml-1.5 tw-mt-0.5 tw-flex tw-flex-col tw-items-start">
        <CopyChip icon={MailIcon} value={contact.email} label="email" small />
        <CopyChip icon={PhoneIcon} value={contact.phone} label="phone" small />
      </div>
    </div>
  );
}

function PaymentCell({ r, density }) {
  const b = r.money?.booking || {};
  const due = balanceDue(r);
  let main;
  switch (b.paymentState) {
    case 'paid':
      main = (
        <span className="tw-whitespace-nowrap">
          <span className="tw-font-medium tw-text-green-700">Paid</span> <span className="tw-tabular-nums tw-text-neutral-500">{formatCurrency(b.paid)}</span>
        </span>
      );
      break;
    case 'overdue':
      main = (
        <span className="tw-whitespace-nowrap tw-font-medium tw-tabular-nums tw-text-red-700">
          Overdue · {formatCurrency(due)} due
        </span>
      );
      break;
    case 'balance_due':
      main = <span className="tw-whitespace-nowrap tw-font-medium tw-tabular-nums tw-text-amber-700">{formatCurrency(due)} due</span>;
      break;
    case 'pending':
      main = <span className="tw-whitespace-nowrap tw-font-medium tw-text-blue-700">Payment pending</span>;
      break;
    case 'refunded':
      main = <span className="tw-whitespace-nowrap tw-text-neutral-500">Refunded</span>;
      break;
    case 'not_charged':
      main = <span className="tw-whitespace-nowrap tw-text-neutral-400">Not charged</span>;
      break;
    default:
      main = <Empty />;
  }
  return (
    <Stack density={density}>
      {main}
      <SharedBy r={r} />
    </Stack>
  );
}

// "n of the forms asked": up to 4; fewer for commuters (no key deposit) and programs that skip a form.
function DocumentsCell({ r }) {
  const missing = r.documents?.missing || [];
  const { required, received } = documentChecklist(r);
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-2" title={missing.length ? `Missing: ${missing.join(', ')}` : 'All documents in'}>
      <span className="tw-flex tw-gap-0.5" aria-hidden>
        {Array.from({ length: required }, (_, i) => (
          <span key={i} className={`tw-h-1.5 tw-w-3 tw-rounded-full ${i < received ? 'tw-bg-green-600' : 'tw-bg-neutral-300'}`} />
        ))}
      </span>
      <span className={`tw-text-sm tw-tabular-nums ${received === required ? 'tw-text-neutral-500' : 'tw-font-medium tw-text-amber-700'}`}>
        {received} of {required}
      </span>
    </span>
  );
}

function MedicalReviewCell({ r }) {
  const label = medicalReviewLabel(r);
  if (label === 'Not required' || label === 'No document') return <span className="tw-whitespace-nowrap tw-text-neutral-400">{label}</span>;
  return label === 'Needs review' ? (
    <span className="tw-inline-flex tw-whitespace-nowrap tw-rounded-full tw-bg-amber-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-amber-700">Needs review</span>
  ) : (
    <span className="tw-inline-flex tw-whitespace-nowrap tw-rounded-full tw-bg-green-50 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-700">Approved</span>
  );
}

function AllergyCell({ r }) {
  const { allergy, allergyDeadly } = r.participant;
  const deadly = Number(allergyDeadly) === 1;
  if (!allergy && !deadly) return <Empty />;
  if (isNone(allergy) && !deadly) return <Text value={allergy} tone="tw-text-neutral-400" />;
  return (
    <span
      className={`tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 ${deadly ? 'tw-font-medium tw-text-red-700' : 'tw-text-neutral-900'}`}
      title={deadly ? `Life-threatening: ${allergy || 'allergy'}` : allergy}>
      {deadly && <ShieldAlertIcon className="tw-h-4 tw-w-4 tw-shrink-0" aria-label="Life-threatening" />}
      <span className="tw-truncate">{allergy || 'Life-threatening'}</span>
    </span>
  );
}

const baseDefs = [
  {
    key: 'bookingDate', width: 148, compactWidth: 196,
    render: (r, ctx) =>
      ctx.density === 'compact' ? (
        <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-neutral-500">
          {formatDate(r.bookingDate)} <span className="tw-text-xs tw-text-neutral-400">· {formatTime(r.bookingDate)}</span>
        </span>
      ) : (
        <div className="tw-whitespace-nowrap tw-tabular-nums">
          <p className="tw-text-neutral-900">{formatDate(r.bookingDate)}</p>
          <p className="tw-mt-0.5 tw-text-xs tw-text-neutral-400">{formatTime(r.bookingDate)}</p>
        </div>
      ),
  },
  {
    key: 'participant', width: 240, compactWidth: 236,
    render: (r, ctx) => {
      const cancelled = isCancelledRow(r);
      const p = r.participant;
      const gender = String(p.gender).toLowerCase();
      const GenderIcon = gender.startsWith('m') ? MarsIcon : gender.startsWith('f') ? VenusIcon : null;
      const nameButton = (
        <button
          type="button"
          onClick={() => ctx.onOpen(r.id)}
          className={`tw-truncate tw-text-left tw-font-medium tw-underline-offset-2 tw-transition-colors hover:tw-text-brand-600 hover:tw-underline ${cancelled ? 'tw-text-neutral-400 tw-line-through' : 'tw-text-neutral-900'}`}>
          {fullName(r) || 'Unnamed participant'}
        </button>
      );
      const genderIcon = GenderIcon ? (
        <GenderIcon className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 ${GenderIcon === MarsIcon ? 'tw-text-blue-700' : 'tw-text-red-700/70'}`} aria-label={p.gender} />
      ) : null;
      const facts = [p.age !== null && p.age !== '' ? `Age ${p.age}` : '', p.grade ? `Grade ${p.grade}` : ''].filter(Boolean).join(' · ');
      if (ctx.density === 'compact') {
        return (
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
            {nameButton}
            {genderIcon}
            {facts && <span className="tw-shrink-0 tw-whitespace-nowrap tw-text-xs tw-text-neutral-400">{facts}</span>}
          </div>
        );
      }
      return (
        <div className="tw-min-w-0">
          <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
            {nameButton}
            {genderIcon}
          </div>
          {facts && <p className="tw-mt-0.5 tw-truncate tw-text-xs tw-text-neutral-400">{facts}</p>}
        </div>
      );
    },
  },
  { key: 'bookingNumber', width: 160, render: r => (r.bookingNumber ? <CopyChip icon={HashIcon} value={r.bookingNumber} label="booking number" /> : <Empty />) },
  {
    key: 'program', width: 240,
    render: (r, ctx) => {
      const p = r.program;
      const name = (
        <span className="tw-block tw-truncate tw-text-neutral-900" title={p.programName}>
          {p.programName || '—'}
          {p.isDeleted && <span className="tw-ml-1 tw-text-xs tw-text-neutral-400">(deleted)</span>}
        </span>
      );
      if (ctx.density === 'compact') return name;
      return (
        <div className="tw-min-w-0">
          {name}
          <p className="tw-mt-0.5 tw-text-xs tw-text-neutral-400">{formatDayRange(p.startDate, p.endDate)}</p>
        </div>
      );
    },
  },
  { key: 'status', width: 140, render: r => <StatusBadge status={r.status} title={r.status === 'Pending' ? 'Payment pending' : undefined} /> },
  { key: 'statusNote', width: 210, render: (r, ctx) => <StatusNoteCell value={r.statusNote} camperName={fullName(r)} onSave={text => ctx.onSaveStatusNote(r, text)} /> },
  {
    key: 'stay', width: 200,
    render: r =>
      r.stay === 'Overnight' ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-neutral-900">
          <MoonIcon className="tw-h-4 tw-w-4 tw-text-blue-700" aria-hidden />
          Overnight
        </span>
      ) : r.stay === 'Commuter' ? (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-neutral-900">
          <SunIcon className="tw-h-4 tw-w-4 tw-text-amber-600" aria-hidden />
          Commuter
        </span>
      ) : (
        <Empty />
      ),
  },
  { key: 'age', width: 96, render: r => <Text value={r.participant.age ?? ''} /> },
  { key: 'grade', width: 110, render: r => <Text value={r.participant.grade} /> },
  { key: 'dob', width: 150, render: r => (r.participant.dob ? <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-neutral-900">{formatDay(r.participant.dob)}</span> : <Empty />) },
  { key: 'gender', width: 110, render: r => <Text value={r.participant.gender} /> },
  { key: 'primaryGuardian', width: 260, compactWidth: 410, render: (r, ctx) => <ContactCell contact={r.primaryGuardian} density={ctx.density} emphasize /> },
  { key: 'secondaryGuardian', width: 260, compactWidth: 410, render: (r, ctx) => <ContactCell contact={r.secondaryGuardian} density={ctx.density} /> },
  { key: 'address', width: 220, render: r => <Text value={r.address.street} /> },
  { key: 'apt', width: 132, render: r => <Text value={r.address.apt} /> },
  { key: 'city', width: 140, render: r => <Text value={r.address.city} /> },
  { key: 'state', width: 96, render: r => <Text value={r.address.state} /> },
  { key: 'zip', width: 116, render: r => <Text value={r.address.zip} tone="tw-tabular-nums tw-text-neutral-900" /> },
  {
    key: 'extras', width: 228,
    render: r => {
      const titles = r.extras.map(e => e.title).filter(Boolean);
      return titles.length ? (
        <span className="tw-inline-flex tw-max-w-full tw-items-center tw-gap-1.5" title={titles.join(', ')}>
          <Tag>{titles[0]}</Tag>
          {titles.length > 1 && <span className="tw-text-xs tw-text-neutral-400">+{titles.length - 1}</span>}
        </span>
      ) : (
        <Empty />
      );
    },
  },
  {
    key: 'additionalCharge', width: 168,
    render: r => {
      const amount = Number(r.money?.participantFees?.additionalCharges) || 0;
      return amount ? <span className="tw-tabular-nums tw-text-neutral-900">+{formatCurrency(amount)}</span> : <Empty />;
    },
  },
  { key: 'tshirtSize', width: 140, render: r => <SizeTag value={r.participant.tshirtSize} /> },
  { key: 'shortsSize', width: 136, render: r => <SizeTag value={r.participant.shortsSize} /> },
  { key: 'payment', width: 190, compactWidth: 260, render: (r, ctx) => <PaymentCell r={r} density={ctx.density} /> },
  {
    key: 'paymentType', width: 170,
    render: (r, ctx) => {
      const b = r.money?.booking || {};
      if (!b.paymentMethod && !b.isPaymentPlan) return <Empty />;
      return (
        <Stack density={ctx.density}>
          {b.paymentMethod && <span className="tw-whitespace-nowrap tw-text-neutral-500">{b.paymentMethod}</span>}
          {b.isPaymentPlan && <Tag>Payment plan{b.paymentsLeft ? ` · ${b.paymentsLeft} left` : ''}</Tag>}
        </Stack>
      );
    },
  },
  {
    key: 'couponCode', width: 144,
    render: r =>
      r.money?.booking?.couponCode ? (
        <span className="tw-rounded tw-bg-sand-50 tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[11px] tw-text-neutral-500 tw-ring-1 tw-ring-inset tw-ring-neutral-200">{r.money.booking.couponCode}</span>
      ) : (
        <Empty />
      ),
  },
  {
    key: 'couponAmount', width: 152,
    render: r => {
      const amount = Number(r.money?.participantFees?.couponDiscount) || 0;
      return amount ? <span className="tw-tabular-nums tw-text-green-700">−{formatCurrency(amount)}</span> : <Empty />;
    },
  },
  { key: 'insuranceCompany', width: 170, render: r => (r.participant.insuranceCompany ? <Text value={r.participant.insuranceCompany} /> : <span className="tw-whitespace-nowrap tw-text-amber-700">Not on file</span>) },
  { key: 'documents', width: 150, render: r => <DocumentsCell r={r} /> },
  { key: 'medicalReview', width: 150, render: r => <MedicalReviewCell r={r} /> },
  { key: 'medical', width: 230, render: r => <Text value={r.participant.medical} tone={isNone(r.participant.medical) ? 'tw-text-neutral-400' : 'tw-text-red-700'} /> },
  { key: 'allergy', width: 230, render: r => <AllergyCell r={r} /> },
  { key: 'groupCode', width: 130, render: r => <Text value={r.participant.groupCode} /> },
];

const baseByKey = Object.fromEntries(baseDefs.map(d => [d.key, d]));

// Question N / Answer N (utils/columns): the row's Nth stored answer. Wording of a removed question is grey.
function answerPairDef(key) {
  const pair = parseAnswerPairKey(key);
  if (!pair) return null;
  const { n } = pair;
  if (pair.kind === 'answer') return { key, width: 200, render: r => <Text value={answerText(r, n)} /> };
  return {
    key,
    width: 220,
    render: r => {
      const question = questionText(r, n);
      return <Text value={question} tone={question === REMOVED_QUESTION ? 'tw-italic tw-text-neutral-400' : 'tw-text-neutral-500'} />;
    },
  };
}

// Definitions for the given keys. A key with no definition (e.g. from an outdated stored layout) is skipped.
export function columnDefsFor(keys) {
  return keys.map(key => baseByKey[key] || answerPairDef(key)).filter(Boolean);
}
